import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApiService, { NotFoundError } from './services/ApiService';
import UrlService from './services/UrlService';
import DecryptionError from './services/DecryptionError';
import NotFoundScreen from './components/NotFoundScreen';
import { useQuicksort } from './hooks/useQuicksort';
import QuicksortModal from './components/QuicksortModal';
import VoteOptionsRenderer from './components/VoteOptionsRenderer';
import LinkText from './components/LinkText';
import LanguageSwitcher from './components/LanguageSwitcher';
import { usePageMetadata } from './hooks/usePageMetadata';


function RankedElectionCreateVote() {
    const { t, i18n } = useTranslation(['voting', 'common', 'election']);
    const [rankedElection, setRankedElection] = useState({});
    const [options, setOptions] = useState([]);
    const [voterName, setVoterName] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [isDecryptionError, setIsDecryptionError] = useState(false);
    const [isNotFoundError, setIsNotFoundError] = useState(false);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Quicksort functionality
    const {
        isActive: isQuicksortActive,
        currentComparison,
        progress: quicksortProgress,
        startSort,
        cancelSort,
        makeChoice
    } = useQuicksort();

    // Page metadata
    const { title, description } = usePageMetadata(rankedElection);

    useEffect(() => {
        // Extract admin keys from URL
        UrlService.extractAndStoreAdminKeys(location, id);
        // Extract encryption keys from URL
        UrlService.extractAndStoreEncryptionKeys(location, id);

        setLoading(true);
        ApiService.getElection(id)
            .then(data => {
                setRankedElection(data);
                // Initialize options with isVetoed and rank connection fields
                const initializedOptions = (data.options || []).map(option => ({
                    ...option,
                    isVetoed: false,
                    isConnectedToNext: false, // Track if this option is connected to the next one
                    rankGroup: null // Track which rank group this option belongs to
                }));
                setOptions(initializedOptions);
                setLoading(false);
                setError(null);
                setIsDecryptionError(false);

                // Sync URL with correct encryption params from localStorage
                UrlService.syncEncryptionKeysWithUrl(id);
            })
            .catch(error => {
                console.error('Error fetching ranked election:', error);
                if (error instanceof NotFoundError) {
                    setIsNotFoundError(true);
                } else if (error instanceof DecryptionError) {
                    setError(t('common:errors.decryptionFailed'));
                    setIsDecryptionError(true);
                } else {
                    setError(t('voting:errors.loadFailed'));
                }
                setLoading(false);
            });
    }, [id, location]); // eslint-disable-line react-hooks/exhaustive-deps


    const handleStartSort = async () => {
        try {
            // Only sort ranked (non-vetoed) options
            const rankedOptions = options.filter(opt => !opt.isVetoed);
            const vetoedOptions = options.filter(opt => opt.isVetoed);
            const sortedRankedOptions = await startSort(rankedOptions);

            // Disconnect all sorted options - quicksort creates a completely new ranking
            const disconnectedSortedOptions = sortedRankedOptions.map(option => ({
                ...option,
                isConnectedToNext: false
            }));

            setOptions([...disconnectedSortedOptions, ...vetoedOptions]);
        } catch (error) {
            if (error.message !== 'Sort cancelled') {
                console.error('Error during quicksort:', error);
                setError(t('voting:errors.sortFailed'));
            }
        }
    };

    // Calculate rank considering connections (sports-style ranking)
    function calculateOptionRank(option, index, rankedOptions) {
        // If this option is connected to the previous one, use the previous option's rank
        if (index > 0 && rankedOptions[index - 1].isConnectedToNext) {
            return calculateOptionRank(rankedOptions[index - 1], index - 1, rankedOptions);
        }
        // Sports-style ranking: count actual positions, not just rank groups
        // Each item takes up a position, so the rank is index + 1
        return index + 1;
    }

    async function handleSubmitVote() {
        setSubmitting(true);
        setError(null);

        // Separate ranked and vetoed options
        const rankedOptions = options.filter(opt => !opt.isVetoed);
        const vetoedOptions = options.filter(opt => opt.isVetoed);

        // Calculate the highest rank used by ranked options (considering connections)
        let highestRank = 0;
        rankedOptions.forEach((option, index) => {
            const rank = calculateOptionRank(option, index, rankedOptions);
            if (rank > highestRank) {
                highestRank = rank;
            }
        });

        // Vetoed options get the next rank after all ranked options
        const vetoRank = highestRank + 1;

        const voteOptions = [
            ...rankedOptions.map((option, index) => ({
                rankedElectionOptionId: option.id,
                rank: calculateOptionRank(option, index, rankedOptions),
                isVetoed: false
            })),
            ...vetoedOptions.map(option => ({
                rankedElectionOptionId: option.id,
                rank: vetoRank,
                isVetoed: true
            }))
        ];

        const voteData = {
            options: voteOptions,
            voterName: voterName.trim() || null
        };

        try {
            const result = await ApiService.createVote(id, voteData);
            navigate(UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/votes/${result.id}`, id));
        } catch (error) {
            console.error('Error submitting vote:', error);
            setError(t('voting:errors.submitFailed'));
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="vote-creation-container">
                <div className="container-fluid h-100">
                    <div className="row h-100 justify-content-center align-items-center">
                        <div className="col-12 text-center">
                            <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">{t('voting:states.loading')}</span>
                            </div>
                            <p className="text-light mt-3">{t('voting:states.loadingOptions')}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show not found error screen if election was not found
    if (isNotFoundError) {
        return <NotFoundScreen type="election" />;
    }

    // Show decryption error screen if decryption failed
    if (isDecryptionError) {
        return (
            <div className="vote-creation-container">
                <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                    <LanguageSwitcher />
                </div>

                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="vote-creation-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">{t('common:errors.decryptionTitle')}</h4>
                                    <p>{t('common:errors.decryptionMessage')}</p>
                                    <hr />
                                    <p className="mb-0">{t('common:errors.decryptionAction')}</p>
                                </div>
                                <div className="mt-3">
                                    <Link to="/" className="btn btn-outline-secondary">
                                        {t('common:navigation.backToHome')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !rankedElection.title) {
        return (
            <div className="vote-creation-container">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="vote-creation-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                                <Link to="/" className="btn btn-outline-secondary">
                                    {t('voting:navigation.backToHome')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Check if current language is RTL (Arabic)
    const isRTL = i18n.language === 'ar';

    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <div className={`vote-creation-container ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                <LanguageSwitcher />
            </div>
            
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 col-xl-8">
                        {/* Navigation */}
                        <div className="vote-creation-nav mb-4 pt-4">
                            <Link to="/" className="btn btn-outline-light btn-sm me-2">
                                {t('voting:navigation.backToHome')}
                            </Link>
                            <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}`, id)} className="btn btn-outline-light btn-sm">
                                {t('voting:navigation.backToElection')}
                            </Link>
                        </div>

                        {/* Election Info Card */}
                        <div className="vote-creation-card mb-4">
                            <div className="vote-creation-header">
                                <h1 className="vote-creation-title">{t('voting:cast.title')}</h1>
                                <div className="election-info">
                                    <h2 className="election-title">{rankedElection.title}</h2>
                                    {rankedElection.description && (
                                        <p className="election-description"><LinkText text={rankedElection.description} /></p>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* Voting Interface */}
                        {options.length > 0 && (
                            <VoteOptionsRenderer
                                options={options}
                                onOptionsChange={setOptions}
                                onStartSort={handleStartSort}
                                isQuicksortActive={isQuicksortActive}
                                disabled={submitting}
                                title={t('voting:cast.subtitle')}
                                subtitle={t('voting:cast.instructions')}
                                enableVeto={true}
                                showImagePreviews={true}
                            />
                        )}
                        
                        {options.length === 0 && !loading && !error && (
                            <div className="vote-creation-card mb-4">
                                <div className="text-center py-4 text-muted">
                                    {t('voting:states.noOptions')}
                                </div>
                            </div>
                        )}

                        {/* Actions Card */}
                        <div className="vote-creation-card mb-4">
                            {error && (
                                <div className="alert alert-danger mb-3" role="alert">
                                    {error}
                                </div>
                            )}
                            
                            {/* Voter Name Input */}
                            <div className="mb-4">
                                <label htmlFor="voterName" className="form-label">
                                    <strong>{t('voting:cast.voterName')}</strong>
                                </label>
                                <input
                                    type="text"
                                    id="voterName"
                                    className="form-control"
                                    placeholder={t('voting:form.voterNamePlaceholder')}
                                    value={voterName}
                                    onChange={(e) => setVoterName(e.target.value)}
                                    disabled={submitting}
                                    maxLength={255}
                                />
                                <div className="form-text text-muted">
                                    {t('voting:cast.voterNameHelp')}
                                </div>
                            </div>
                            
                            <div className="vote-actions">
                                <button
                                    className="btn btn-success btn-lg vote-submit-btn"
                                    onClick={handleSubmitVote}
                                    disabled={submitting || options.filter(opt => !opt.isVetoed).length === 0}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            {t('voting:cast.submittingVote')}
                                        </>
                                    ) : (
                                        t('voting:cast.submitVote')
                                    )}
                                </button>

                                <Link
                                    to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}`, id)}
                                    className="btn btn-outline-secondary btn-lg"
                                    style={{ pointerEvents: submitting ? 'none' : 'auto' }}
                                >
                                    {t('voting:navigation.cancel')}
                                </Link>
                            </div>
                            
                            <div className="mt-3 text-center">
                                <small className="text-muted">
                                    {t('voting:cast.editHelp')}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Quicksort Modal */}
            <QuicksortModal
                show={isQuicksortActive}
                currentComparison={currentComparison}
                progress={quicksortProgress}
                onChoice={makeChoice}
                onCancel={cancelSort}
            />
            </div>
        </>
    );
}

export default RankedElectionCreateVote;